import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { PageDrawing } from '../../../../../models/page-drawing';
import { CommunicationService } from '../../../../../services/communication.service';
import { Subscription } from 'rxjs';
import _ from 'lodash';
import { FabricService } from '../../../../../services/fabric.service';
import { DrawEditMode } from '../../../../../utils/page-edit-modes/draw-edit-mode';
import { ApplicationStateService } from '../../../../../services/application-state.service';
import { DrawAction, DrawActionElement, DrawOptions } from '../../../../../models/draw-action';
import { Position } from '../../../../../models/position';
import { FreehandDrawAction } from '../../../../../models/draw-actions/freehand-draw-action';
import { Colorable } from '../../../../../models/colorable';
import { sidebarConfig } from '../../../../../../environments/configs/sidebar-config';
import { KeyValues } from '../../../../../utils/key-values';
import { TooltipUtils } from '../../../../../utils/tooltip-utils';
import { NgStyle } from '@angular/common';
import { FormsModule } from '@angular/forms';

declare const fabric;

@Component({
    selector: 'app-page-canvas',
    templateUrl: './page-canvas.component.html',
    styleUrls: ['./page-canvas.component.scss'],
    standalone: true,
    imports: [
        NgStyle,
        FormsModule
    ]
})
export class PageCanvasComponent implements OnInit, OnDestroy {

    @Input() pageDrawing: PageDrawing;
    @Input() width: number;
    @Input() height: number;

    drawActionElements = DrawActionElement;

    canvas: any; // fabric.Canvas;
    canvasContainer;

    drawModeSubscription: Subscription;
    colorChangedSubscription: Subscription;
    borderWidthChangedSubscription: Subscription;

    debouncedOnResize: (() => void) & _.Cancelable;
    debouncedSave: ((a, b, c, d) => void) & _.Cancelable;
    debouncedSetSize: ((width: number, height: number) => void) & _.Cancelable;

    isMouseDown: boolean;
    clickPosition: Position;
    movePosition: Position;
    drawElement: any;

    textPosition: Position;
    textText: string;
    textEditable = false;

    showDeleteIcon = false;
    deleteIconPosition: Position;
    private upperCanvasSelector: string;

    constructor(public fabricService: FabricService,
                private communicationService: CommunicationService,
                private applicationStateService: ApplicationStateService) {

        //the buffer of +200 is necessary to ensure that the current width and heights are available
        //and the page drawings are correctly positioned after resize
        this.debouncedOnResize = _.debounce(() => this.onResize(), sidebarConfig.sidebarTransitionSpeed + 200);
        this.debouncedSave = _.debounce(this.saveCanvas, sidebarConfig.sidebarTransitionSpeed);
        this.debouncedSetSize = _.debounce(this.setCanvasSize, 50);

        this.drawModeSubscription = this.communicationService.editMoveableBookFeatureAnnounced$.subscribe(() => {

            if (this.isDrawingModeActive()) {
                this.unlockCanvas();
            } else {
                this.lockCanvas();
                this.clearSelection();
            }

        });

        this.colorChangedSubscription = this.communicationService.drawModeColorChangedSource$.subscribe(color => {
            this.setColorOfActiveCanvasElement(color as Colorable);
        });

        this.borderWidthChangedSubscription = this.communicationService.drawModeBorderWidthChangedSource$.subscribe(width => {
            this.setBorderWidthOfActiveCanvasElements(width as number);
        });
    }

    ngOnInit(): void {

        setTimeout(() => {
            if (this.pageDrawing) {

                this.initCanvas();
                this.scaleCanvas(this.width, this.height);
                if (!this.isDrawingModeActive()) {
                    this.lockCanvas();
                }
                this.upperCanvasSelector = `#drawingCanvas-${this.pageDrawing.pageId} + .upper-canvas`;

                this.registerCanvasEvents();
            }
        }, 10);
    }

    ngOnDestroy(): void {
        this.drawModeSubscription.unsubscribe();
        this.colorChangedSubscription.unsubscribe();
        this.borderWidthChangedSubscription.unsubscribe();
        this.unregisterEvents();
    }

    onKeyDown($event): void {

        switch ($event.keyCode) {
            case 8: // Backspace
            case 46: // Delete
                this.deleteSelectionOnCanvas();
                break;
            default:
                // do nothing
                return;
        }
    }

    onResize(): void {
        const width = this.width / this.applicationStateService.zoomLevel;
        const height = this.height / this.applicationStateService.zoomLevel;
        this.canvas.setWidth(width);
        this.canvas.setHeight(height);

        if (this.pageDrawing.fabricObject) {

            const clone = JSON.parse(JSON.stringify(this.pageDrawing.fabricObject));
            if (clone.objects) {
                clone.objects.forEach(drawElement => {
                    this.scaleCanvasElement(drawElement, width, height);
                    this.setSelectionStyleOfDrawElement(drawElement);
                });
            }
            this.canvas.loadFromJSON(JSON.stringify(clone));
            this.canvas.setZoom(this.applicationStateService.zoomLevel);
            this.scaleCanvas(this.width, this.height);
        }
    }

    /**
     * If the focus of the input field is lost, the text element is created.
     */
    onFocusOut($event?): void {

        if ($event) {
            $event.stopPropagation();
        }

        if (this.isDrawingModeActive()) {
            this.textEditable = false;
            const drawOptions = new DrawOptions();
            drawOptions.stampingPosition = new Position();
            drawOptions.stampingPosition.x = this.textPosition.x;
            drawOptions.stampingPosition.y = this.textPosition.y;
            drawOptions.color = this.fabricService.getCurrentColor().color;
            drawOptions.borderWidth = this.fabricService.getCurrentBorderWidth();
            drawOptions.text = this.textText;
            this.drawElement = this.fabricService.executeCurrentDrawAction(this.canvas, drawOptions);
            this.saveCanvas();
            this.unlockCanvas();
        }
    }

    /**
     * Used to focus to the input field, because somehow the element in not focused automatically when it is clicked.
     * @param {string} elementId
     */
    onClick(elementId: string): void {
        $(`#${elementId}`)
            .focus();
    }

    /**
     * Scales the fabric.Canvas and its elements.
     */
    scaleCanvas(width: number, height: number): void {

        if (!this.canvas) {
            return;
        }
        this.canvas.discardActiveObject();
        this.debouncedSetSize(width, height);
        this.canvas.setZoom(this.applicationStateService.zoomLevel);
    }

    /**
     * Disables the drawing of the canvas elements.
     */
    lockCanvas(): void {
        if (this.canvas) {
            this.canvas.selection = false;
            const canvasElements = this.canvas.getObjects();
            this.canvasContainer.css('z-index', 0);

            canvasElements.forEach(canvasElement => this.lockCanvasElement(canvasElement));
        }
    }

    /**
     * Enables the drawing of the canvas elements.
     */
    unlockCanvas(): void {
        if (this.canvas) {
            this.canvas.selection = true;
            const canvasElements = this.canvas.getObjects();
            this.canvasContainer.css('z-index', 30);
            canvasElements.forEach(canvasElement => this.unlockCanvasElement(canvasElement));
        }
    }

    removeSelectedDrawElement(): void {
        const activeObject = this.canvas.getActiveObject();

        if (activeObject) {
            if (!activeObject._objects) {
                this.canvas.remove(activeObject);
            }
        }

        this.saveCanvas();
    }

    exitOnEnter($event: KeyboardEvent): void {
        if (KeyValues.getKey($event) === KeyValues.ENTER) {
            this.onFocusOut();
        }
    }

    preventDragOffCanvas(e): void {
        const target = e.target;
        const maxY = Math.max(target.oCoords.tl.y, target.oCoords.tr.y, target.oCoords.bl.y, target.oCoords.br.y);
        const minY = Math.min(target.oCoords.tl.y, target.oCoords.tr.y, target.oCoords.bl.y, target.oCoords.br.y);
        const maxX = Math.max(target.oCoords.tl.x, target.oCoords.tr.x, target.oCoords.bl.x, target.oCoords.br.x);
        const minX = Math.min(target.oCoords.tl.x, target.oCoords.tr.x, target.oCoords.bl.x, target.oCoords.br.x);

        const zoomLevel = this.applicationStateService.zoomLevel;

        target.top = Math.min(Math.max((target.oCoords.tl.y - minY) / zoomLevel, target.top),
            (this.height - (maxY - target.oCoords.tl.y)) / zoomLevel);
        target.left = Math.min(Math.max((target.oCoords.tl.x - minX) / zoomLevel, target.left),
            (this.width - (maxX - target.oCoords.tl.x)) / zoomLevel);
    }

    private setCanvasSize(width: number, height: number): void {
        this.canvas.setWidth(width);
        this.canvas.setHeight(height);
    }

    /**
     * Creates a new fabric.Canvas, determines it's width and height and draws the canvas objects.
     */
    private initCanvas(): void {
        this.canvas = new fabric.Canvas(`drawingCanvas-${this.pageDrawing.pageId}`, {
            allowTouchScrolling: this.isTouchDevice() &&
                this.applicationStateService.isScrollMode()
        });
        const width = this.width / this.applicationStateService.zoomLevel;
        const height = this.height / this.applicationStateService.zoomLevel;
        this.canvas.setWidth(width);
        this.canvas.setHeight(height);
        this.canvas.setZoom(this.applicationStateService.zoomLevel);
        this.canvas.selectionBorderColor = '#000';
        this.canvas.selectionColor = 'rgba(255,255,255,0.2)';

        let cornerSize = DrawAction.defaults.cornerSize;

        if (this.isTouchDevice()) {
            cornerSize *= 2;
        }

        fabric.Object.prototype.cornerSize = cornerSize;
        fabric.Object.prototype.borderColor = DrawAction.defaults.borderColor;
        fabric.Object.prototype.cornerColor = DrawAction.defaults.cornerColor;
        fabric.Object.prototype.transparentCorners = DrawAction.defaults.transparentCorners;

        if (this.pageDrawing.fabricObject) {

            const clone = JSON.parse(JSON.stringify(this.pageDrawing.fabricObject));
            if (clone.objects) {
                clone.objects.forEach(drawElement => {
                    this.scaleCanvasElement(drawElement, width, height);
                    this.setSelectionStyleOfDrawElement(drawElement);
                });
            }
            this.canvas.loadFromJSON(JSON.stringify(clone));
        }

        this.canvasContainer = $('.canvas-container, .canvas-container *');
        this.canvasContainer
            .css('position', 'absolute')
            .css('top', 0);
    }

    /**
     * Scales a single fabric.Canvas Element
     * @param canvasElement
     * @param canvasWidth
     * @param canvasHeight
     */
    private scaleCanvasElement(canvasElement: any, canvasWidth: number, canvasHeight: number): void {

        canvasElement.left = canvasElement.left * canvasWidth;
        canvasElement.top = canvasElement.top * canvasHeight;
        canvasElement.width = canvasElement.width * canvasWidth;
        canvasElement.height = canvasElement.height * canvasHeight;
        this.setSelectionStyleOfDrawElement(canvasElement);

        if (canvasElement.radius) {
            canvasElement.radius = canvasElement.radius * canvasWidth;
        }

        if (canvasElement.x2) {
            canvasElement.width = canvasElement.width / canvasWidth;
            canvasElement.height = canvasElement.height / canvasHeight;
            canvasElement.x1 = canvasElement.x1 * canvasWidth;
            canvasElement.x2 = canvasElement.x2 * canvasWidth;
            canvasElement.y1 = canvasElement.y1 * canvasHeight;
            canvasElement.y2 = canvasElement.y2 * canvasHeight;
        }

        if (canvasElement.path) {

            canvasElement.width = canvasElement.width / canvasWidth;
            canvasElement.height = canvasElement.height / canvasHeight;
            canvasElement.path.forEach(pathElement => {
                switch (pathElement[0]) {
                    case 'L':
                    case 'M':
                        pathElement[1] = pathElement[1] * canvasWidth;
                        pathElement[2] = pathElement[2] * canvasHeight;
                        break;
                    case 'Q':
                        pathElement[1] = pathElement[1] * canvasWidth;
                        pathElement[2] = pathElement[2] * canvasHeight;
                        pathElement[3] = pathElement[3] * canvasWidth;
                        pathElement[4] = pathElement[4] * canvasHeight;
                        break;
                    case 'C':
                        pathElement[1] = pathElement[1] * canvasWidth;
                        pathElement[2] = pathElement[2] * canvasHeight;
                        pathElement[3] = pathElement[3] * canvasWidth;
                        pathElement[4] = pathElement[4] * canvasHeight;
                        pathElement[5] = pathElement[5] * canvasWidth;
                        pathElement[6] = pathElement[6] * canvasHeight;
                        break;
                    default:
                        break;
                }
            });
        }

        if (canvasElement.setCoords) {
            canvasElement.setCoords();
        }

    }

    /**
     * Prevents a canvas element from being changed.
     * @param canvasElement
     */
    private lockCanvasElement(canvasElement: any): void {
        canvasElement.selectable = false;
        canvasElement.hoverCursor = 'default';
        canvasElement.lockMovementX = true;
        canvasElement.lockMovementY = true;
        canvasElement.lockScalingX = true;
        canvasElement.lockScalingY = true;
        canvasElement.lockUniScaling = true;
        canvasElement.lockRotation = true;
    }

    /**
     * Enables a canvas element to be changed.
     * @param canvasElement
     */
    private unlockCanvasElement(canvasElement: any): void {
        canvasElement.selectable = true;
        canvasElement.hoverCursor = 'move';
        canvasElement.lockMovementX = false;
        canvasElement.lockMovementY = false;
        canvasElement.lockScalingX = false;
        canvasElement.lockScalingY = false;
        canvasElement.lockUniScaling = false;
        canvasElement.lockRotation = false;
    }

    /**
     * Checks if the application is currenlty in the DrawingMode.
     * @returns {boolean}
     */
    private isDrawingModeActive(): boolean {
        return this.applicationStateService.pageEditMode && this.applicationStateService.pageEditMode instanceof DrawEditMode;
    }

    /**
     * Deletes all canvas elements which are selected (set as active).
     */
    private deleteSelectionOnCanvas(): void {

        const activeObject = this.canvas.getActiveObject();

        if (activeObject) {
            if (activeObject._objects) {
                activeObject._objects.forEach(ele => this.canvas.remove(ele));
            }

            this.canvas.remove(activeObject);
        }

        this.saveCanvas();
    }

    /**
     * Determines all parameters and then let the FabricService execute the DrawAction.
     * @param options
     */
    private executeDrawAction(options): void {

        TooltipUtils.hideAllTooltips();

        if (this.isDrawingModeActive()) {
            const drawOptions = new DrawOptions();
            drawOptions.stampingPosition = new Position();
            drawOptions.stampingPosition.x = options.e.offsetX;
            drawOptions.stampingPosition.y = options.e.offsetY;
            drawOptions.color = this.fabricService.getCurrentColor().color;
            drawOptions.borderWidth = this.fabricService.getCurrentBorderWidth();
            this.drawElement = this.fabricService.executeCurrentDrawAction(this.canvas, drawOptions, this.pageDrawing);
        }
    }

    /**
     * Initialised all necessary fabric.Canvas events.
     */
    private registerCanvasEvents(): void {
        const thiz = this;

        this.canvas.on('selection:created', () => {
            this.onSelectionCreated();
        });

        this.canvas.on('selection:updated', () => {
            this.onSelectionUpdated();
        });

        this.canvas.on('selection:cleared', () => {
            this.onSelectionCleared();
        });

        this.canvas.on('object:moving', obj => {
            this.onSelectionUpdated();
            this.preventDragOffCanvas(obj);
            this.calculateDeleteIconPosition();
            this.debouncedSave(this.pageDrawing, this.canvas, this.width, this.height);
        });

        this.canvas.on('object:scaling', () => {
            this.onSelectionUpdated();
            this.calculateDeleteIconPosition();
            this.debouncedSave(this.pageDrawing, this.canvas, this.width, this.height);
        });

        this.canvas.on('object:rotating', () => {
            this.onSelectionUpdated();
            this.calculateDeleteIconPosition();
            this.debouncedSave(this.pageDrawing, this.canvas, this.width, this.height);
        });

        if (!this.isTouchDevice()) {

            this.canvas.on('mouse:down', options => {
                thiz.onMouseDown(options);
            });

            this.canvas.on('mouse:move', options => {
                thiz.onMouseMove(options);

            });

            this.canvas.on('mouse:up', () => {
                thiz.onMouseUp();
            });

            return;
        }

        this.canvasContainer
            .find(this.upperCanvasSelector)
            .on('touchstart', event => {

                if (!this.isDrawingModeActive()) {
                    return;
                }

                /*const options: any = {};
                options.e = {};
                const canvasOffset = this.canvasContainer.offset();
                options.e.offsetX = event.originalEvent.touches[0].pageX - canvasOffset.left;
                options.e.offsetY = event.originalEvent.touches[0].pageY - canvasOffset.top;*/
                thiz.onMouseDown(event);
            });

        this.canvasContainer.on('touchmove', event => {
            if (!this.isDrawingModeActive()) {
                return;
            }
            /*const options: any = {};
            options.e = {};
            const canvasOffset = this.canvasContainer.offset();
            options.e.offsetX = event.originalEvent.touches[0].pageX - canvasOffset.left;
            options.e.offsetY = event.originalEvent.touches[0].pageY - canvasOffset.top;
            */
            thiz.onMouseMove(event);
        });

        this.canvasContainer
            .find(this.upperCanvasSelector)
            .on('touchend', () => {
                if (!this.isDrawingModeActive()) {
                    return;
                }
                thiz.onMouseUp();
            });

    }

    private unregisterEvents(): void {
        if (this.canvas) {
            this.canvas.off('after:render');
            this.canvas.off('mouse:down');
            this.canvas.off('mouse:move');
            this.canvas.off('mouse:up');
            this.canvas.off('selection:created');
            this.canvas.off('selection:updated');
            this.canvas.off('selection:cleared');
            this.canvas.off('object:moving');
            this.canvas.off('object:scaling');
            this.canvas.off('object:rotating');

            if (this.canvasContainer) {
                const upperCanvas = this.canvasContainer.find(this.upperCanvasSelector);

                if (upperCanvas) {
                    upperCanvas.off('touchstart');
                    upperCanvas.off('touchmove');
                    upperCanvas.off('touchend');
                }
            }
        }
    }

    /**
     *  Saves the position on the canvas on which the MouseDown was performed.
     *  Initialised the new fabric.Object.
     * @param options
     */
    private onMouseDown(options): void {
        this.isMouseDown = true;

        const pos: any = {};
        pos.e = {};
        pos.e.offsetX = this.canvas.getPointer(options).x;
        pos.e.offsetY = this.canvas.getPointer(options).y;

        this.executeDrawAction(pos);

        if (this.textEditable) {
            this.onFocusOut();
        }

        if (this.drawElement) {

            this.canvas.selection = false;
            this.clearSelection();
            this.canvas._objects.forEach(ele => {
                ele.selectable = false;
                ele.lockMovementX = true;
                ele.lockMovementY = true;
            });

            if (this.isStamp()) {
                this.drawElement.left = this.canvas.getPointer(options).x;
                this.drawElement.top = this.canvas.getPointer(options).y;
            }

            this.clickPosition = new Position();
            this.clickPosition.x = this.drawElement.left;
            this.clickPosition.y = this.drawElement.top;

            if (this.isText()) {

                this.textEditable = true;
                const position = new Position();
                position.x = this.drawElement.left;
                position.y = this.drawElement.top;
                this.textPosition = position;
                this.textText = undefined;

                setTimeout(() => {
                    $(`#input-${this.pageDrawing.pageId}`)
                        .focus();
                }, 100);

            } else {
                this.textEditable = false;
            }
        }
    }

    /**
     * Calculates the new values of the current fabric.Object and set the values (left, top, width, height, radius)
     * accordingly.
     * @param options
     */
    private onMouseMove(options): void {

        this.canvas.isDrawingMode = this.fabricService.getCurrentDrawAction() instanceof FreehandDrawAction;
        this.canvas.freeDrawingBrush.color = this.fabricService.getCurrentColor().color
            .getColorString();
        this.canvas.freeDrawingBrush.width = this.fabricService.getCurrentBorderWidth();
        this.canvas.freeDrawingBrush.decimate = 2;

        if (this.isMouseDown && this.drawElement) {

            this.movePosition = new Position();
            this.movePosition.x = Math.min(Math.max(0, this.canvas.getPointer(options).x), this.canvas.getWidth() - 5);
            this.movePosition.y = Math.min(Math.max(0, this.canvas.getPointer(options).y), this.canvas.getHeight() - 5);

            let clickPosX = this.clickPosition.x;
            let clickPosY = this.clickPosition.y;

            const offset = 16;
            clickPosX = clickPosX + offset;
            clickPosY = clickPosY + offset;

            const minX = Math.min(clickPosX, this.movePosition.x);
            const minY = Math.min(clickPosY, this.movePosition.y);
            const maxX = Math.max(clickPosX, this.movePosition.x);
            const maxY = Math.max(clickPosY, this.movePosition.y);

            if (this.isCircle()) {

                const radius = Math.sqrt(Math.pow((maxX - minX), 2) + Math.pow((maxY - minY), 2));
                const left = clickPosX - radius;
                const top = clickPosY - radius;

                this.drawElement.set({
                    left,
                    top,
                    radius
                });

            } else if (this.isLine()) {

                this.drawElement.set({
                    x1: this.clickPosition.x,
                    y1: this.clickPosition.y,
                    x2: this.movePosition.x,
                    y2: this.movePosition.y
                });

            } else if (this.isStamp()) {

                const activeObject = this.canvas.getActiveObject();
                if (activeObject) {
                    activeObject.set({
                        left: minX,
                        top: minY,
                        width: maxX - minX,
                        height: maxY - minY
                    });
                }

            } else {

                this.drawElement.set({
                    left: minX,
                    top: minY,
                    width: maxX - minX,
                    height: maxY - minY
                });
            }

            if (this.drawElement.setCoords) {
                this.drawElement.setCoords();
            }
            this.canvas.renderAll();
        }
    }

    /**
     * Removes unneeded data when the interaction ends.
     */
    private onMouseUp(): void {
        this.isMouseDown = false;
        this.movePosition = undefined;
        if (this.drawElement ||
            this.fabricService.getCurrentDrawAction() instanceof FreehandDrawAction) {

            this.reduceSelectionToLastElement();
            this.canvas._objects.forEach(ele => {
                ele.selectable = true;
                ele.lockMovementX = false;
                ele.lockMovementY = false;
            });

            this.saveCanvas();
        }
        this.canvas.selection = true;
        this.drawElement = undefined;
    }

    /**
     * Checks if current drawElement is a circle.
     */
    private isCircle(): boolean {
        return this.drawElement && this.drawElement.radius;
    }

    /**
     * Checks if current drawElement is a TextElement.
     */
    private isText(): boolean {
        return this.drawElement && this.drawElement.text;
    }

    /**
     * Checks if current drawElement is a stamp.
     */
    private isStamp(): boolean {
        return this.drawElement && this.drawElement.stamp;
    }

    /**
     * Checks if current drawElement is a line.
     */
    private isLine(): boolean {
        return this.drawElement && this.drawElement.x1 !== undefined;
    }

    /**
     * Checks if device uses touchevents.
     */
    private isTouchDevice(): boolean {
        return $('html')
            .hasClass('touchevents');
    }

    /**
     * Sets the border color of the active canvas elements.
     * @param color The new color of the elements.
     */
    private setColorOfActiveCanvasElement(color: Colorable): void {

        const activeObject = this.canvas.getActiveObject();

        if (activeObject) {
            if (activeObject._objects) {
                activeObject._objects.forEach(ele => this.setColorOfCanvasElement(ele, color));
            } else {
                this.setColorOfCanvasElement(activeObject, color);
            }
        }

        this.canvas.renderAll();
        this.saveCanvas();
    }

    /**
     * Sets the color of a draw element.
     * @param drawElement
     * @param color
     */
    private setColorOfCanvasElement(drawElement: any, color: Colorable): void {

        if (drawElement.type === 'path' && drawElement.stamping) {
            drawElement.set(
                {
                    fill: color.color.getColorString(),
                    stroke: color.color.getColorString()
                });
        } else if (drawElement.text) {
            drawElement.set(
                {
                    fill: color.color.getColorString()
                });
        } else {
            drawElement.set(
                {
                    stroke: color.color.getColorString()
                });
        }
    }

    /**
     * Sets border width of active canvas elements.
     * @param width The new border width of the elements.
     */
    private setBorderWidthOfActiveCanvasElements(width: number): void {
        const activeObject = this.canvas.getActiveObject();

        if (activeObject) {
            if (activeObject._objects) {
                activeObject._objects.forEach(ele => this.setBorderWidthOfActiveCanvasElement(ele, width));
            } else {
                this.setBorderWidthOfActiveCanvasElement(activeObject, width);
            }
        }

        this.canvas.renderAll();
        this.saveCanvas();
    }

    /**
     * Sets border width of active canvas elements.
     * @param drawElement
     * @param width The new border width of the elements.
     */
    private setBorderWidthOfActiveCanvasElement(drawElement: any, width: number): void {

        if (drawElement.text) {
            let fontWeight: number;
            switch (width) {
                case 1:
                case 2:
                    fontWeight = 100;
                    break;
                case 3:
                    fontWeight = 300;
                    break;
                case 5:
                    fontWeight = 500;
                    break;
                case 8:
                    fontWeight = 600;
                    break;
                case 10:
                    fontWeight = 700;
                    break;
                default:
                    fontWeight = 100;
            }

            drawElement.set({ strokeWidth: width, fontWeight });
        } else {
            drawElement.set({ strokeWidth: width });
        }

    }

    /**
     * Sets the Selection style of the element.
     * @param drawElement
     */
    private setSelectionStyleOfDrawElement(drawElement: any): void {
        let cornerSize = DrawAction.defaults.cornerSize;

        if (this.isTouchDevice()) {
            cornerSize *= 2;
        }

        drawElement.cornerSize = cornerSize;
        drawElement.borderColor = DrawAction.defaults.borderColor;
        drawElement.cornerColor = DrawAction.defaults.cornerColor;
        drawElement.transparentCorners = DrawAction.defaults.transparentCorners;
    }

    /**
     * Calculates the position of the delete icon.
     */
    private onSelectionCreated(): void {
        this.calculateDeleteIconPosition();
    }

    /**
     * Calculates the position of the delete icon.
     */
    private onSelectionUpdated(): void {
        this.onSelectionCreated();
    }

    /**
     * Remove the delete icon.
     */
    private onSelectionCleared(): void {
        this.showDeleteIcon = false;
        this.deleteIconPosition = undefined;
    }

    /**
     * Clears the current selection of DrawElements.
     */
    private clearSelection(): void {
        if (this.canvas) {
            this.canvas.discardActiveObject();
            this.canvas.renderAll();
        }
    }

    /**
     * Saves the canvas.
     */
    private saveCanvas(): void {
        this.fabricService.savePageDrawing(
            this.pageDrawing,
            this.canvas,
            this.width / this.applicationStateService.zoomLevel,
            this.height / this.applicationStateService.zoomLevel);
    }

    /**
     * Reduces the active canvas elements to the last element of the selection (the last added element).
     * If only one element is selected, nothing happens. This is used to only select the latest created element.
     */
    private reduceSelectionToLastElement(): void {
        const activeObject = this.canvas.getActiveObject();
        if (activeObject && activeObject._objects) {
            this.canvas.setActiveObject(activeObject._objects[activeObject._objects.length - 1]);
        }
    }

    private calculateDeleteIconPosition(): void {
        const activeObject = this.canvas.getActiveObject();

        if (!activeObject) {
            return;
        }

        this.deleteIconPosition = new Position();

        if (activeObject && !activeObject._objects) {
            const oCoords = activeObject.calcACoords();
            const coordsArray = _.sortBy([oCoords.tl, oCoords.tr, oCoords.bl, oCoords.br],
                pos => {
                    return pos.x + pos.y * 10;
                });

            this.deleteIconPosition.x = coordsArray[coordsArray.length - 1].x;
            this.deleteIconPosition.y = coordsArray[coordsArray.length - 1].y;
            this.showDeleteIcon = true;
        }
    }

}
